import { cn } from '@/lib/utils';
import React from 'react';
import { CheckCircle2, XCircle, X, Info } from 'lucide-react';

type AlertVariant = 'info' | 'error' | 'success';

interface AlertProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  className?: string;
  id?: string;
  onClose?: () => void;
}

const variantStyles = {
  success: {
    container: 'bg-gradient-to-r from-[#18E78F]/5 to-[#18E78F]/10 border-[#18E78F]/20 dark:from-[#18E78F]/5 dark:to-[#18E78F]/10 dark:border-[#18E78F]/15',
    icon: 'text-[#18E78F]/80 dark:text-[#18E78F]/70',
    text: 'text-foreground/90 dark:text-foreground/85',
    accent: 'border-l-2 border-l-[#18E78F]/40'
  },
  info: {
    container: 'bg-gradient-to-r from-blue-500/5 to-blue-500/10 border-blue-500/20 dark:from-blue-500/5 dark:to-blue-500/10 dark:border-blue-500/15',
    icon: 'text-blue-500/80 dark:text-blue-400/70',
    text: 'text-foreground/90 dark:text-foreground/85',
    accent: 'border-l-2 border-l-blue-500/40'
  },
  error: {
    container: 'bg-gradient-to-r from-red-500/5 to-red-500/10 border-red-500/20 dark:from-red-500/5 dark:to-red-500/10 dark:border-red-500/15',
    icon: 'text-red-500/80 dark:text-red-400/70',
    text: 'text-foreground/90 dark:text-foreground/85',
    accent: 'border-l-2 border-l-red-500/40'
  }
};

const icons = {
  success: <CheckCircle2 className="w-4 h-4" />,
  info: <Info className="w-4 h-4" />,
  error: <XCircle className="w-4 h-4" />
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  children,
  className,
  id,
  onClose
}) => {
  const styles = variantStyles[variant];

  return (
    <div
      id={id}
      className={cn(
        'border rounded-lg backdrop-blur-md shadow-sm transition-all duration-300',
        'animate-in slide-in-from-top-2 fade-in',
        'max-w-xs', // ✅ Reduced from max-w-sm to max-w-xs (320px instead of 384px)
        styles.container,
        styles.accent,
        className
      )}
    >
      <div className="flex items-start gap-2 p-4"> {/* ✅ Reduced gap and padding */}
        <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
          {icons[variant]}
        </div>
        <div className="flex-1 min-w-0">
            <p className={cn('text-[12.7px] leading-snug font-semibold', styles.text)}> {/* ✅ Slightly bolder text */}
            {children}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={cn(
              'flex-shrink-0 p-0.5 rounded-md transition-all hover:bg-black/5 dark:hover:bg-white/5 opacity-60 hover:opacity-100',
              styles.icon
            )}
            aria-label="Close alert"
          >
            <X className="w-3 h-3" /> {/* ✅ Smaller close button */}
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
